import { useSearchParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/data';
import { BreadcrumbListSchema, WebPageSchema } from '@/components/base/SchemaOrg';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'boundary';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(searchParams.get('tab') || 'boundary');
  }, [searchParams]);

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => a.category === activeTab);
  }, [activeTab]);

  const currentCategory = categories.find((c) => c.id === activeTab);
  const baseUrl = 'https://www.maternal-psych-guide.cn';

  const handleTabChange = (tabId: string) => {
    setSearchParams({ tab: tabId });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <ScrollToTop />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: baseUrl },
          { name: '文章分类', url: `${baseUrl}/category` },
          { name: currentCategory?.name || '', url: `${baseUrl}/category?tab=${activeTab}` },
        ]}
      />
      <WebPageSchema
        title={`${currentCategory?.name || '文章分类'} - 母子关系心理健康指南`}
        description={currentCategory?.description || '浏览母子关系心理健康相关的专业文章分类'}
        url={`${baseUrl}/category?tab=${activeTab}`}
      />

      <Navbar />

      <main>
        {/* Page Header */}
        <section className="bg-background-100 pt-24 pb-12">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '文章分类' },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4 mb-3">
              文章分类
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              按照专业分类浏览文章，深入了解母子关系心理健康的各个方面
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 ${
                    activeTab === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Current Category Info */}
        {currentCategory && (
          <section className="py-8 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700 flex-shrink-0">
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-1">
                    {currentCategory.name}
                  </h2>
                  <p className="text-sm text-foreground-500">{currentCategory.description}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mx-auto mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章，敬请期待</p>
              </div>
            )}
          </div>
        </section>

        {/* All Categories Overview */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">
              浏览其他分类
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.filter((c) => c.id !== activeTab).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleTabChange(cat.id)}
                  className="text-left p-5 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className={cat.icon}></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-900">{cat.name}</h3>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}